import { useEffect, useRef, useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import Card from '@mui/material/Card';
import TableRow from '@mui/material/TableRow';
import {
  Avatar,
  Box,
  Button,
  Container,
  IconButton,
  Stack,
  TablePagination,
} from '@mui/material';
import Page from '../../components/Page';
import { useAppSelector, useAppDispatch } from '../../store';
import {
  setLimit,
  setSkip,
  setCurrentPage,
  setStatus,
} from '../../store/propertyCategory/propertyCategorySlice';
import {
  findPropertyCategory,
  createPropertyCategory,
  patchPropertyCategory,
} from '../../store/propertyCategory/api';
import Loader from '../../components/Loader';
import Label from '../../components/Label';
import Iconify from '../../components/Iconify';
import { useFormik } from 'formik';
import useToggle from '../../hooks/useToggle';
import { omit } from 'lodash';
import PropertyCategoryFormModal from './PropertyCategoryModal';
import {
  IInitialState,
  PropertyCategory,
} from '../../store/propertyCategory/api/interface';
import CloseIcon from '@mui/icons-material/Close';
import { getAssetURl } from '../../utils/url';
import Searchbar from '../../components/Searchbar';
import {
  reset as resetPropertyCategory,
  setStatus as setPropertyCategoryStatus,
} from '../../store/propertyCategory/propertyCategorySlice';
import propertyCategorySchema from '../../validation/propertyCategorySchema';
import {
  reset as resetSelectPropertyCategoryReset,
  setStatus as selectPropertyCategorySetStatus,
} from '../../store/selectPropertyCategory/selectPropertyCategorySlice';

const Index: React.FC = () => {
  const dispatch = useAppDispatch();
  const { data, totalRecords, limit, currentPage, status, skip, isLoading } =
    useAppSelector((state) => state.propertyCategory);
  const { toggle, onOpen, onClose } = useToggle();
  const [search, setSearch] = useState<string>('');

  useEffect(() => {
    if (status === 'idle') {
      dispatch(
        findPropertyCategory({
          limit,
          skip,

          ...(search && { search: search }),
        })
      );
    }
  }, [dispatch, status, limit, skip, search]);

  useEffect(() => {
    dispatch(resetPropertyCategory());
  }, [search]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearch(e.target.value);
  };

  const {
    handleSubmit,
    values,
    setFieldValue,
    resetForm,
    isSubmitting,
    errors,
    touched,
    handleChange,
    setValues,
  } = useFormik<IInitialState>({
    initialValues: {
      id: '',
      labelEn: '',
      labelAr: '',
      icon: '',
      position: '',
      activeState: true,
      isLand: false,
    },
    validationSchema: propertyCategorySchema,
    onSubmit: async (values) => {
      try {
        if (values.id) {
          await dispatch(patchPropertyCategory(values)).unwrap();
        }
        if (!values.id) {
          const reqBody = omit(values, ['id']);
          await dispatch(createPropertyCategory(reqBody)).unwrap();
        }
        onClose();
        resetForm();
        dispatch(setStatus({ status: 'idle' }));
        dispatch(resetSelectPropertyCategoryReset());
        dispatch(selectPropertyCategorySetStatus({ status: 'idle' }));
      } catch (error: any) {}
    },
  });

  const handleUpdate = (value: PropertyCategory): void => {
    onOpen();
    setValues({
      id: value.id,
      labelEn: value?.labelEn,
      labelAr: value?.labelAr,
      position: value?.position,
      icon: value?.icon,
      activeState: value?.activeState,
      isLand: value?.isLand ?? false,
    });
  };

  const handlePageChange = (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number
  ): void => {
    dispatch(setSkip({ skip: limit * newPage }));
    dispatch(setCurrentPage({ currentPage: newPage }));
    dispatch(setPropertyCategoryStatus({ status: 'idle' }));
  };
  const handleRowsPerPageChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    dispatch(setLimit({ limit: +event.target.value }));
  };

  const boxRef = useRef<HTMLDivElement>(null);
  const [openLightBox, setOpenLightBox] = useState(false);
  const [imageState, setImageState] = useState<string>();

  const handleOpenLightBox = (imageObject: string) => {
    setOpenLightBox(true);
    setImageState(imageObject);
  };

  const handleCloseLightBox = () => {
    setOpenLightBox(false);
    setImageState('');
  };

  return (
    <>
      <Page title={'Property Category: List'}>
        <Container maxWidth={'xl'}>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-end'
            spacing={{ xs: 0.5, sm: 1.5 }}
            my={1}
          >
            <Button
              variant='contained'
              startIcon={<Iconify icon={'eva:plus-fill'} />}
              onClick={onOpen}
            >
              {'New Property Category'}
            </Button>
          </Stack>

          <Card>
            <Searchbar handleSearch={handleSearch} />
              <TableContainer
                sx={{ width: '100%', overflowX: 'auto', position: 'relative', pt: 2 }}
              >
                <Table size={'medium'} sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell align='center'>{'Icon'}</TableCell>
                      <TableCell align='center'>{'Position'}</TableCell>
                      <TableCell align='center'>{'Label EN'}</TableCell>
                      <TableCell align='center'>{'Label AR'}</TableCell>
                      <TableCell align='center'>{'Status'}</TableCell>
                      <TableCell align='center'>{'Land'}</TableCell>
                      <TableCell align='center'>{'Action'}</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {isLoading ? (
                      <TableRow hover>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          <Loader />
                        </TableCell>
                      </TableRow>
                    ) : data?.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={7}
                          align='center'
                        >
                          {'No record found'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      data?.map((propertyCategory) => (
                        <TableRow key={propertyCategory.id}>
                          <TableCell
                            align='center'
                            sx={{
                              display: 'table-cell',
                              justifyContent: 'center',
                            }}
                          >
                            {propertyCategory?.icon && (
                              <Avatar
                                onClick={() =>
                                  handleOpenLightBox(propertyCategory?.icon)
                                }
                                alt={propertyCategory?.icon}
                                imgProps={{ crossOrigin: 'anonymous' }}
                                src={getAssetURl(propertyCategory?.icon)}
                                sx={{
                                  borderRadius: 'unset',
                                  m: 1,
                                  objectFit: 'contain',
                                  '& img': { objectFit: 'contain' },
                                }}
                              />
                            )}
                          </TableCell>
                          <TableCell align='center'>
                            {propertyCategory?.position}
                          </TableCell>
                          <TableCell align='center'>
                            {propertyCategory?.labelEn}
                          </TableCell>
                          <TableCell align='center'>
                            {propertyCategory?.labelAr}
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={
                                propertyCategory.activeState
                                  ? 'success'
                                  : 'error'
                              }
                            >
                              {propertyCategory.activeState
                                ? 'Active'
                                : 'InActive'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <Label
                              variant='ghost'
                              color={
                                propertyCategory.isLand ? 'success' : 'error'
                              }
                            >
                              {propertyCategory.isLand ? 'True' : 'False'}
                            </Label>
                          </TableCell>
                          <TableCell align='center'>
                            <IconButton
                              aria-label='edit'
                              onClick={() => handleUpdate(propertyCategory)}
                            >
                              <Iconify icon={'eva:edit-fill'} />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

            <Box sx={{ position: 'relative' }}>
              <TablePagination
                rowsPerPageOptions={[25, 50, 100]}
                component='div'
                count={totalRecords}
                rowsPerPage={limit}
                page={Math.min(Math.round(totalRecords / limit), currentPage)}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
              />
            </Box>
          </Card>
          <PropertyCategoryFormModal
            touched={touched}
            errors={errors}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            onClose={onClose}
            resetForm={resetForm}
            setFieldValue={setFieldValue}
            toggle={toggle}
            values={values}
          />
        </Container>

        {openLightBox && (
          <Box
            sx={{ pt: 'env(safe-area-inset-top) !important' }}
            id='myModal'
            className='modal'
          >
            <span className='close cursor'>
              <CloseIcon
                fontSize='large'
                onClick={handleCloseLightBox}
                sx={{ mt: 'env(safe-area-inset-top) !important' }}
              />
            </span>
            <div
              className={`modal-content ${
                openLightBox ? 'display-block ' : 'display-none'
              }`}
            >
              {imageState && (
                <div className='vertical-center-div'>
                  <div
                    ref={boxRef}
                    className={`mySlides ${
                      openLightBox ? 'display-block ' : 'display-none'
                    }`}
                  >
                    <img
                      id={imageState}
                      src={getAssetURl(imageState)}
                      width='100%'
                      height='100%'
                      crossOrigin='anonymous'
                      alt={imageState}
                    />
                  </div>
                </div>
              )}
            </div>
          </Box>
        )}
      </Page>
    </>
  );
};

export default Index;
